import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ShieldWarning, X } from "@phosphor-icons/react";

const DISMISS_KEY = "vs-mfa-banner-dismissed-at";
const DISMISS_HOURS = 24;

/**
 * Top-of-content banner pushing the current user to enable 2FA when:
 *   • they have not already (mfa_enabled !== true), AND
 *   • their role is in app_settings.security.mfa_required_roles (HARD warning) OR
 *     their role is super_admin (SOFT recommendation).
 *
 * Dismissable for 24 hours via localStorage so it doesn't nag too often.
 */
export default function Mfa2FAReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requiredRoles, setRequiredRoles] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const last = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (last && Date.now() - last < DISMISS_HOURS * 3600 * 1000) {
      setDismissed(true);
    }
    if (user?.role === "super_admin") {
      api.get("/admin/settings/security")
        .then((r) => setRequiredRoles(new Set(r.data?.mfa_required_roles || [])))
        .catch(() => setRequiredRoles(new Set()));
    } else {
      // Non-super_admin can't load the security config — assume not required
      // unless the backend returns 428 on a protected endpoint.
      setRequiredRoles(new Set());
    }
  }, [user?.role]);

  if (!user || user.mfa_enabled || dismissed || requiredRoles === null) return null;

  const isRequired = requiredRoles.has(user.role);
  const isRecommended = user.role === "super_admin" && !isRequired;
  if (!isRequired && !isRecommended) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setDismissed(true);
  };

  return (
    <div
      className={`mb-4 rounded-lg border-l-4 p-4 flex items-start gap-3 ${
        isRequired
          ? "border-red-500 bg-red-50 text-red-900"
          : "border-amber-500 bg-amber-50 text-amber-900"
      }`}
      data-testid="mfa-reminder-banner"
      role="alert"
    >
      <ShieldWarning size={22} weight="bold" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-sm">
          {isRequired
            ? "Two-Factor Authentication is required for your role"
            : "Recommended: turn on Two-Factor Authentication"}
        </div>
        <div className="text-xs mt-1 leading-relaxed">
          {isRequired
            ? "You'll be blocked from protected endpoints until you complete 2FA setup. Takes 30 seconds with Google Authenticator or Authy."
            : "Super-admin accounts have access to every trust. Enable 2FA to protect against credential theft."}
        </div>
        <button
          onClick={() => navigate("/app/security")}
          className={`mt-2.5 text-xs font-semibold uppercase tracking-widest font-mono px-3 py-1.5 rounded-md transition-colors ${
            isRequired
              ? "bg-red-900 text-red-50 hover:bg-red-800"
              : "bg-amber-900 text-amber-50 hover:bg-amber-800"
          }`}
          data-testid="mfa-reminder-cta"
        >
          Enable 2FA →
        </button>
      </div>
      {!isRequired && (
        <button
          onClick={handleDismiss}
          className="text-amber-900/60 hover:text-amber-900 p-1"
          aria-label="Dismiss"
          data-testid="mfa-reminder-dismiss"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
